import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { adminApi, errorMessage } from "../lib/api";
import { PhoneVerify } from "../components/landing/PhoneVerify";
import { BookingForm, useCatalog } from "../components/hk/BookingForm";
import { MyBookings } from "../components/hk/MyBookings";
import { inr } from "../components/landing/usePricing";

const Login = ({ onLogin }) => {
  const [phone, setPhone] = useState("");
  const [token, setToken] = useState("");
  useEffect(() => {
    if (!token) return;
    adminApi.post("/hk/login", { phone, phone_verification_token: token }).then(() => onLogin()).catch((e) => { toast.error(errorMessage(e)); setToken(""); });
  }, [token, phone, onLogin]);
  return (
    <div data-testid="hk-login" className="max-w-md space-y-4 border border-white/10 bg-[#1E293B]/60 p-6">
      <p className="font-semibold text-white">Verify your mobile to book</p>
      <PhoneVerify prefix="hk-login" label="Mobile Number" phone={phone} token={token} onPhone={(v) => { setPhone(v); setToken(""); }} onToken={setToken} />
    </div>
  );
};

export default function BookCleaning() {
  const catalog = useCatalog();
  const [items, setItems] = useState(null);
  const load = useCallback(() => adminApi.get("/hk/bookings").then((r) => setItems(r.data)).catch(() => setItems(null)), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="min-h-screen bg-[#0B1120] px-4 pb-20 font-body text-slate-100 sm:px-6">
      <header className="mx-auto flex max-w-5xl items-center justify-between py-5">
        <a href="/" data-testid="hk-home" className="flex items-center gap-2.5">
          <span className="font-heading text-lg font-extrabold text-white">Balangir Group <span className="font-mono text-xs font-normal text-slate-500">/ services</span></span>
        </a>
        {items && <button onClick={() => adminApi.post("/hk/logout").then(() => setItems(null))} data-testid="hk-logout" className="text-sm text-slate-300 hover:text-orange-400">Log out</button>}
      </header>
      <main className="mx-auto max-w-5xl space-y-8">
        <div>
          <h1 className="flex items-center gap-3 font-heading text-4xl font-extrabold text-white sm:text-5xl"><Sparkles className="h-8 w-8 text-orange-400" />Book a service</h1>
          <p className="mt-3 text-slate-400">Deep cleaning, water tank cleaning, AC service and construction labour by verified teams. Pay 30% to book, the rest after the job. <span className="text-emerald-300">Pay securely by UPI; payments are verified automatically.</span></p>
          <a href="/?join=housekeeping" data-testid="hk-join-employee" className="mt-4 inline-block border border-orange-500/60 px-4 py-2 text-sm font-semibold text-orange-300 hover:bg-orange-500/10">Join as Housekeeping Employee →</a>
        </div>
        {catalog && [...new Set(catalog.services.map((s) => s.group))].map((g) => (
          <div key={g} data-testid={`hk-catalog-${g.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="space-y-3">
            <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-400">{g}</p>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {catalog.services.filter((s) => s.group === g).map((s) => (
                <div key={s.key} data-testid={`hk-service-${s.key}`} className="overflow-hidden border border-white/10 bg-[#0F172A]">
                  <img src={s.image} alt={s.group} loading="lazy" className="h-28 w-full object-cover" />
                  <div className="p-4"><p className="text-sm font-semibold text-slate-200">{s.label}</p>
                  <p className={`mt-1 font-mono ${s.price ? "text-xl text-orange-300" : "text-sm text-amber-300"}`}>{s.price ? `${inr(s.price)}${s.unit_label ? `/${s.unit_label}` : ""}` : "Price on request"}</p>
                  {s.includes?.length > 0 && <ul data-testid={`hk-includes-${s.key}`} className="mt-2 space-y-0.5 text-xs text-slate-400">{s.includes.map((x) => <li key={x}>✓ {x}</li>)}</ul>}</div></div>
              ))}
            </div>
          </div>
        ))}
        {items === null && <Login onLogin={load} />}
        {items && catalog && <BookingForm catalog={catalog} onCreated={load} />}
        {items && catalog && <MyBookings items={items} reload={load} catalog={catalog} />}
      </main>
    </div>
  );
}
